import React, { useState, useEffect } from 'react';
import { useLogin, useNotify } from 'react-admin';
import { Card, CardContent, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem, Box, CircularProgress } from '@mui/material';
import { authProvider } from './authProvider'; // Import authProvider
import { useRedirect } from 'react-admin';
import config from './config/config';

const CustomLoginPage = () => {
    const [username, setUsername] = useState(''); // Email
    const [password, setPassword] = useState('');
    const [tenant, setTenant] = useState('viv_wines_and_spirits'); // Tenant ID for sign up
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isSignUp, setIsSignUp] = useState(false); // New state for sign up
    const [confirmation_code, setVerificationCode] = useState(''); // State for verification code
    const [isVerifying, setIsVerifying] = useState(false); // State to track verification step

    const login = useLogin();
    const notify = useNotify();

    useEffect(() => {
        const fetchTenants = async () => {
            try {
                const url = `${config.authApiUrl}/tenants`;
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error('Failed to fetch tenants');
                }
                const data = await response.json();
                setTenants(data.tenants || []); // Provide empty array fallback
            } catch (error) {
                setError(error.message);
                setTenants([]); // Set empty array on error
            } finally {
                setLoading(false);
            }
        };

        fetchTenants();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        if (isSignUp) {
            // Sign Up logic
            authProvider.signup({ email: username, password, tenant })
                .then(() => {
                    notify('Sign-up successful! Please enter the verification code sent to your email.', { type: 'info' });
                    setIsVerifying(true); // Switch to verification code input
                })
                .catch((error) => {
                    notify('Sign-up failed: ' + error.message, { type: 'error' });
                    setError(error.message);
                });
        } else {
            // Login logic
            login({ username, password, tenant })
                .catch((error) => {
                    notify('Invalid username, password, or tenant', { type: 'error' });
                    setError(error.message);
                });
        }
    };

    const redirect = useRedirect();

    const handleVerifyCode = (e) => {
        e.preventDefault();
        setError('');

        // Verification code logic
        console.log({ username, confirmation_code, tenant })
        authProvider.verify_code({ username, confirmation_code, tenant }) 
            .then(() => {
                notify('Verification successful! Now you can logged in.', { type: 'info' });
                // redirect('/');
                setIsVerifying(false); // Switch back to login form
                setIsSignUp(false);    // Ensure sign-up state is reset
            })
            .catch((error) => {
                notify('Verification failed: ' + error.message, { type: 'error' });
                setError(error.message);
            });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
            <Card>
                <CardContent>
                    <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                        <img 
                            src="/logo.png" 
                            alt="ViV Logo" 
                            style={{ width: 240 }} 
                        />
                        <Typography variant="h5" align="center" gutterBottom>
                            Welcome to the ViV Client Desktop
                        </Typography>
                    </Box>
                    <Typography variant="h6" align="center" gutterBottom>
                        {isSignUp ? (isVerifying ? 'Verify Your Account' : 'Sign Up') : 'Login'}
                    </Typography>
                    <form onSubmit={isVerifying ? handleVerifyCode : handleSubmit}>
                        <TextField
                            label="Email"
                            fullWidth
                            margin="normal"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                        <TextField
                            label="Password"
                            fullWidth
                            margin="normal"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        {isSignUp && (
                            <TextField
                                label="Tenant ID"
                                fullWidth
                                margin="normal"
                                value={tenant}
                                onChange={(e) => setTenant(e.target.value)}
                            />
                        )}
                        {!isSignUp && (
                            <Box sx={{ display: 'none' }}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Tenant</InputLabel>
                                    <Select
                                        value="viv_wines_and_spirits" // Hardcoded tenant value
                                        onChange={(e) => setTenant(e.target.value)}
                                    >
                                        {tenants.map((t) => (
                                            <MenuItem key={t} value={t}>
                                                {t}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                        {isSignUp && isVerifying && (
                            <TextField
                                label="Verification Code"
                                fullWidth
                                margin="normal"
                                value={confirmation_code}
                                onChange={(e) => setVerificationCode(e.target.value)}
                            />
                        )}
                        {error && (
                            <Typography color="error" align="center">
                                {error}
                            </Typography>
                        )}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={!username || !password || (isSignUp ? (isVerifying ? !confirmation_code : !tenant) : !tenant)}
                            sx={{ mt: 2 }}
                        >
                            {isVerifying ? 'Verify Code' : (isSignUp ? 'Sign Up' : 'Login')}
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            onClick={() => setIsSignUp(!isSignUp)} // Toggle between sign up and login
                            sx={{ mt: 2 }}
                        >
                            {isSignUp ? 'Already have an account? Login' : 'No account? Sign Up'}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CustomLoginPage;
